import React, { useEffect, useState } from "react";

import { getDeliveries } from "../../api/agent";

const TYPE_LABELS = {
  bug: "Bug",
  evolution: "Évolution",
  idee: "Idée",
  question: "Question",
};

function capitalize(value) {
  if (!value) return "";
  if (value === "unknown") return "Inconnu";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatRequesters(requesters) {
  const names = (requesters || []).map(capitalize).filter(Boolean);
  if (names.length < 2) return names[0] || "";
  return `${names.slice(0, -1).join(", ")} et ${names[names.length - 1]}`;
}

function formatMonth(isoDate) {
  const label = new Intl.DateTimeFormat("fr-FR", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(isoDate));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatDay(isoDate) {
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(isoDate));
}

function groupByMonth(deliveries) {
  const sorted = [...deliveries].sort(
    (left, right) => Date.parse(right.delivered_at) - Date.parse(left.delivered_at),
  );
  const groups = [];

  sorted.forEach((delivery) => {
    const date = new Date(delivery.delivered_at);
    const key = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      group = { key, label: formatMonth(delivery.delivered_at), deliveries: [] };
      groups.push(group);
    }
    group.deliveries.push(delivery);
  });

  return groups;
}

export default function Deliveries({ token }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    getDeliveries(token)
      .then((data) => {
        if (!cancelled) setDeliveries(data.deliveries || []);
      })
      .catch((requestError) => {
        if (cancelled) return;
        if (requestError?.response?.status === 403) {
          setError("Vue réservée à l'équipe");
        } else {
          setError("Impossible de charger les nouveautés");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const groups = groupByMonth(deliveries);

  return (
    <div className="bg-slate-50">
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
        <header className="mb-10">
          <div className="mb-3 flex items-center gap-3">
            <span
              aria-hidden="true"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 text-xl"
            >
              ✨
            </span>
            <h1 className="text-3xl font-bold tracking-tight text-slate-950">Nouveautés</h1>
          </div>
          <p className="max-w-2xl text-slate-600">
            Tout ce qui a été livré à l'équipe, du plus récent au plus ancien.
          </p>
        </header>

        {loading ? (
          <div role="status" className="rounded-xl border border-slate-200 bg-white px-6 py-12 text-center text-slate-600">
            Chargement des nouveautés…
          </div>
        ) : error ? (
          <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-6 py-8 text-center text-red-800">
            {error}
          </div>
        ) : groups.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
            <p className="font-medium text-slate-900">Aucune nouveauté pour le moment</p>
            <p className="mt-1 text-sm text-slate-500">Les prochaines livraisons apparaîtront ici.</p>
          </div>
        ) : (
          <div className="space-y-10">
            {groups.map((group) => (
              <section key={group.key} className="deliveries-month" aria-labelledby={`month-${group.key}`}>
                <div className="mb-4 flex items-baseline justify-between gap-4 border-b border-slate-200 pb-3">
                  <h2 id={`month-${group.key}`} className="text-xl font-semibold text-slate-950">
                    {group.label}
                  </h2>
                  <span className="text-sm text-slate-500">
                    {group.deliveries.length} nouveauté{group.deliveries.length > 1 ? "s" : ""}
                  </span>
                </div>

                <div className="space-y-4">
                  {group.deliveries.map((delivery) => {
                    const requesters = formatRequesters(delivery.requesters);
                    return (
                      <article
                        key={delivery.id}
                        className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
                      >
                        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <h3 className="text-lg font-semibold leading-6 text-slate-950">
                            {delivery.title}
                          </h3>
                          <span className="w-fit shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                            {TYPE_LABELS[delivery.type] || delivery.type}
                          </span>
                        </div>

                        {delivery.public_note ? (
                          <p className="delivery-public-note whitespace-pre-line text-base leading-7 text-slate-700">
                            {delivery.public_note}
                          </p>
                        ) : (
                          // Une livraison sans note reste affichee : l'escamoter
                          // ferait disparaitre du travail reellement fait.
                          <p className="delivery-public-note italic text-sm text-slate-400">
                            Pas de détail renseigné
                          </p>
                        )}

                        <div className="mt-5 flex flex-col gap-1 border-t border-slate-100 pt-4 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
                          {requesters ? <span>Demandé par {requesters}</span> : <span />}
                          <time dateTime={delivery.delivered_at}>{formatDay(delivery.delivered_at)}</time>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
